import * as React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import { listMeetings } from "@/api/client";
import { MeetingView } from "@/api/types";
import { ComicPanel } from "@/components/comic";
import { cn } from "@/lib/utils";

import {
  formatDateLabel,
  formatTime,
  MEETING_STATE_STYLES,
  meetingPivot,
  meetingTitle,
  toUiMeeting,
} from "./shared";

/**
 * Compact homepage-sidebar widget: top few upcoming or in-flight meetings,
 * links through to the full /meetings page.
 */
export function MeetingsAtAGlance() {
  const [meetings, setMeetings] = React.useState<MeetingView[] | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      listMeetings()
        .then((data) => {
          if (!cancelled) setMeetings(data);
        })
        .catch(() => {
          /* sidebar widget — swallow errors, the main page surfaces them */
        });
    refresh();
    const handle = window.setInterval(refresh, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  const now = new Date();
  const top = (meetings ?? [])
    .map(toUiMeeting)
    .filter((m) => meetingPivot(m) >= startOfToday(now))
    .sort((a, b) => meetingPivot(a).getTime() - meetingPivot(b).getTime())
    .slice(0, 4);

  return (
    <ComicPanel title="Meetings" dense>
      <div className="space-y-1.5">
        {meetings === null && (
          <div className="text-xs text-muted-foreground">Loading…</div>
        )}
        {meetings !== null && top.length === 0 && (
          <div className="text-xs text-muted-foreground">
            Nothing upcoming. Quiet on the wire.
          </div>
        )}
        {top.map((m) => {
          const style = MEETING_STATE_STYLES[m.state];
          const when = m.start ?? m.createdAt;
          return (
            <div
              key={m.id}
              className={cn(
                "rounded border-2 border-border p-1.5 text-xs",
                style.bg,
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-comic-headline truncate uppercase">
                  {meetingTitle(m)}
                </div>
                <span
                  className={cn(
                    "shrink-0 rounded border px-1 font-comic-headline text-[9px] uppercase",
                    style.border,
                    "bg-card",
                  )}
                >
                  {style.label}
                </span>
              </div>
              <div className="mt-0.5 text-[10px] text-muted-foreground">
                {formatDateLabel(when)}
                {m.start && ` · ${formatTime(m.start)}`}
              </div>
            </div>
          );
        })}
        <Link
          to="/meetings"
          className="mt-2 flex items-center justify-center gap-1 rounded border border-dashed border-foreground/40 py-1 font-comic-headline text-[10px] uppercase tracking-wide text-muted-foreground hover:bg-muted"
        >
          See all <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
    </ComicPanel>
  );
}

function startOfToday(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}
